import React from "react";
import { Button } from "antd";
import { GitBranch, Lightbulb, Users } from "lucide-react";
import {
  comparisonText,
  constructLabel,
  decisionStatus,
  decisionSummary,
  definingBehaviors,
  discoverySteps,
  discoveryWhy,
  directionWord,
  formatMetric,
  hasStructuredEvidence,
  memberCellValue,
  memberColumns,
  memberWhyText,
  supportingEvidence,
  thresholdText,
} from "./exploreExplainability";
import styles from "./ExplorePanel.module.scss";

const CompareBars = ({ item, audienceLabel, restLabel }) => {
  const left = Number(item.audience_value);
  const right = Number(item.other_value);
  const max = Math.max(left, right, 1);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return null;
  return (
    <div className={styles.evidenceCompare}>
      <div className={styles.evidenceBarRow}>
        <span>{audienceLabel}</span>
        <div className={styles.evidenceTrack}>
          <i className={styles.evidenceFill} style={{ width: `${Math.max(8, (100 * left) / max)}%` }} />
        </div>
        <b>{formatMetric(left)}</b>
      </div>
      <div className={styles.evidenceBarRow}>
        <span>{restLabel}</span>
        <div className={styles.evidenceTrack}>
          <i className={styles.evidenceFillRest} style={{ width: `${Math.max(8, (100 * right) / max)}%` }} />
        </div>
        <span>{formatMetric(right)}</span>
      </div>
    </div>
  );
};

const EvidenceCard = ({ item, kind, audienceLabel, restLabel, inclusion }) => {
  const label = constructLabel(item);
  const compare = comparisonText(item);
  const rule = thresholdText(inclusion || item);
  return (
    <article className={`${styles.evidenceCard} ${kind === "defining" ? styles.evidenceDefining : styles.evidenceSupporting}`}>
      <span className={styles.evidenceKind}>{kind === "defining" ? "Defining behavior" : "Supporting evidence"}</span>
      <h4>{label || "Observed difference"}</h4>
      {compare ? <p className={styles.evidenceCompareText}>{compare} median</p> : null}
      <CompareBars item={item} audienceLabel={audienceLabel} restLabel={restLabel} />
      {directionWord(item) ? <p className={styles.evidenceDir}>{directionWord(item)}</p> : null}
      {kind === "defining" && rule ? <p className={styles.evidenceRule}>{rule}</p> : null}
    </article>
  );
};

const ExplainabilityDetail = ({
  audience,
  inspect,
  t,
  formatCount,
  members,
  onOpenMembers,
}) => {
  const evidence = inspect?.explainability_evidence || audience?.explainability_evidence;
  const defining = definingBehaviors(evidence);
  const supporting = supportingEvidence(evidence);
  const why = discoveryWhy(evidence);
  const decision = decisionSummary(inspect || audience);
  const status = decisionStatus(inspect || audience);
  const steps = discoverySteps(evidence);
  const columns = memberColumns(evidence);
  const inclusion = Array.isArray(evidence?.member_inclusion) ? evidence.member_inclusion : [];
  const preview = (members || []).slice(0, 4);
  const structured = hasStructuredEvidence(evidence);
  const previewCols = `1.1fr ${columns.map(() => "0.8fr").join(" ")} 1.2fr`;

  return (
    <div className={styles.explainLayout}>
      <div className={styles.explainMain}>
        <section className={`${styles.panel} ${styles.whyPanel}`}>
          <h3>
            <Lightbulb size={16} strokeWidth={2} aria-hidden="true" />
            {t("abs.explore_why_discover", "Why did we discover this audience?")}
          </h3>
          <p className={styles.detailWhy}>
            {why ||
              audience?.why ||
              t(
                "abs.explore_why_fallback",
                "This audience is a distinct behavioral group in the file."
              )}
          </p>
        </section>

        <section className={styles.panel}>
          <h3>
            {defining.length > 1
              ? t("abs.explore_defines_plural", "What defines this audience?")
              : t("abs.explore_defines", "What defines this audience?")}
          </h3>
          {defining.length > 1 ? (
            <p className={styles.sectionCap}>
              {t("abs.explore_both_define", "Both behaviors define this audience.")}
            </p>
          ) : null}
          <div className={styles.evidenceStack}>
            {defining.map((item, index) => (
              <React.Fragment key={`${item.construct || "def"}-${index}`}>
                {index > 0 ? <p className={styles.andJoin}>AND</p> : null}
                <EvidenceCard
                  item={item}
                  kind="defining"
                  inclusion={inclusion.find((row) => row.construct === item.construct)}
                  audienceLabel={t("abs.explore_this_audience", "This audience")}
                  restLabel={t("abs.explore_other_customers", "Other customers")}
                />
              </React.Fragment>
            ))}
          </div>
        </section>

        {supporting.length ? (
          <section className={styles.panel}>
            <h3>{t("abs.explore_supporting", "Supporting evidence")}</h3>
            <p className={styles.sectionCap}>
              {t(
                "abs.explore_supporting_cap",
                "Members also differ from the rest of the file on these independent behaviors."
              )}
            </p>
            <div className={styles.evidenceStack}>
              {supporting.map((item, index) => (
                <EvidenceCard
                  key={`${item.construct || "sup"}-${index}`}
                  item={item}
                  kind="supporting"
                  audienceLabel={t("abs.explore_this_audience", "This audience")}
                  restLabel={t("abs.explore_other_customers", "Other customers")}
                />
              ))}
            </div>
          </section>
        ) : null}

        {decision ? (
          <section className={`${styles.panel} ${styles.hypothesisPanel}`}>
            <h3>{t("abs.explore_hypothesis", "Data-supported hypothesis")}</h3>
            {status ? <span className={styles.decisionStatus}>{status.replace(/_/g, " ")}</span> : null}
            <p className={styles.detailWhy}>{decision}</p>
          </section>
        ) : null}

        {structured ? (
          <section className={styles.panel}>
            <h3>
              <GitBranch size={16} strokeWidth={2} aria-hidden="true" />
              {t("abs.explore_how_found", "How we found it")}
            </h3>
            <ol className={styles.foundChain}>
              {steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </section>
        ) : null}
      </div>

      <aside className={styles.explainAside}>
        <section className={styles.panel}>
          <h3>
            <Users size={16} strokeWidth={2} aria-hidden="true" />
            {t("abs.explore_glance", "Audience at a glance")}
          </h3>
          <dl className={styles.profileList}>
            <div>
              <dt>{t("abs.explore_who_size", "Audience size")}</dt>
              <dd>
                {t("abs.explore_people_count", "{{count}} customers", {
                  count: formatCount(inspect?.total ?? audience.size),
                })}
              </dd>
            </div>
            {audience.share != null && audience.share !== "" ? (
              <div>
                <dt>{t("abs.explore_who_share", "Share of this file")}</dt>
                <dd>{`${audience.share}%`}</dd>
              </div>
            ) : null}
          </dl>
        </section>

        <section className={styles.panel}>
          <h3>{t("abs.explore_members_preview", "Members")}</h3>
          <p className={styles.sectionCap}>
            {t(
              "abs.explore_members_why_cap",
              "Why each customer is included, from the audience rule — not the raw CRM row."
            )}
          </p>
          {preview.length ? (
            <div className={styles.memberPreview}>
              <div className={styles.memberPreviewHead} style={{ gridTemplateColumns: previewCols }}>
                <span>{t("abs.col_name", "Name")}</span>
                {columns.map((column) => (
                  <span key={column.key}>{column.label}</span>
                ))}
                <span>{t("abs.explore_why_included", "Why included")}</span>
              </div>
              {preview.map((row) => (
                <div
                  className={styles.memberPreviewRow}
                  key={row.signal_profile_id || row.email}
                  style={{ gridTemplateColumns: previewCols }}
                >
                  <span>{row.display_name || row.email || "—"}</span>
                  {columns.map((column) => (
                    <span key={column.key}>{memberCellValue(row, column) || "—"}</span>
                  ))}
                  <span>{memberWhyText(row.why_included) || "—"}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.saveHint}>
              {t(
                "abs.explore_members_hint",
                "Open the members panel to inspect the rows that match this audience."
              )}
            </p>
          )}
          <Button type="primary" onClick={onOpenMembers} className={styles.membersCta}>
            {t("abs.explore_view_members", "View members")}
          </Button>
        </section>
      </aside>
    </div>
  );
};

export default ExplainabilityDetail;
